/**
 * One tool call, rendered inline in the thread when the "Tool calls" toggle is on. Collapsed to
 * a compact row (name + a hint of the arguments, an error marker); click expands the full
 * arguments and output, monospaced and independently scrollable so a wide `list_skus` result
 * never blows up the message column.
 */

import { AlertTriangle, ChevronDown, ChevronRight } from "lucide-react";
import { useState } from "react";

import type { ToolCallRecord } from "@/lib/api";
import { cn } from "@/lib/utils";

const HINT_MAX = 64;

function argsHint(input: Record<string, unknown> | undefined): string {
  if (!input || Object.keys(input).length === 0) {
    return "";
  }
  const joined = Object.entries(input)
    .map(([key, value]) => `${key}=${JSON.stringify(value)}`)
    .join(", ");
  return joined.length > HINT_MAX ? `${joined.slice(0, HINT_MAX)}…` : joined;
}

export function ToolCallRow({ toolCall }: { toolCall: ToolCallRecord }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="border-border bg-card/70 max-w-[85%] rounded-md border text-xs">
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        aria-expanded={expanded}
        className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left"
      >
        {expanded ? (
          <ChevronDown className="text-muted-foreground h-3 w-3 shrink-0" />
        ) : (
          <ChevronRight className="text-muted-foreground h-3 w-3 shrink-0" />
        )}
        <span className="text-foreground shrink-0 font-mono font-medium">{toolCall.name}</span>
        <span className="text-muted-foreground truncate">{argsHint(toolCall.input)}</span>
        {toolCall.is_error ? (
          <AlertTriangle
            className="text-destructive ml-auto h-3 w-3 shrink-0"
            aria-label="Tool call failed"
          />
        ) : null}
      </button>
      {expanded ? (
        <div className="border-border space-y-2 border-t px-2.5 py-2">
          <div>
            <p className="text-muted-foreground mb-1 text-[10px] tracking-wide uppercase">
              Arguments
            </p>
            <pre className="bg-foreground/5 max-h-40 overflow-auto rounded p-2 font-mono text-[11px] whitespace-pre-wrap">
              {JSON.stringify(toolCall.input ?? {}, null, 2)}
            </pre>
          </div>
          <div>
            <p className="text-muted-foreground mb-1 text-[10px] tracking-wide uppercase">
              {toolCall.is_error ? "Error" : "Output"}
            </p>
            <pre
              className={cn(
                "max-h-40 overflow-auto rounded p-2 font-mono text-[11px] whitespace-pre-wrap",
                toolCall.is_error ? "bg-destructive/10 text-destructive" : "bg-foreground/5",
              )}
            >
              {toolCall.output}
            </pre>
          </div>
        </div>
      ) : null}
    </div>
  );
}
